import { useState, useEffect } from "react";
import { Table, TableBody, TableRow, TableHeader } from "@vds/tables";
import { Tooltip } from "@vds/tooltips";
import styled from "styled-components";
import PropTypes from "prop-types";
import Chip from "../../cases/Chip";
import SkeletonLoader from "../common/SkeletonLoader";
import { TextWrapper, SortIconStyle1, CustomisedTertiaryButton } from "../../../constants/common/StyledGlobalConstants";
import "../../../components/cases/DatePickerCSS.css";
import PageAlignment from "../../../components/cases/PageAlignment";
import { useNavigate } from "react-router-dom";
import TechnicianAppointmentFilter from "./TechnicianAppointmentFilter";
import { useDispatch, useSelector } from "react-redux";
import {
  setTotalNumberOfRecords,
  setAppointmentPerPage,
  setActivePage,
  filteredAppointmentList,
} from "../../../store/actions/schedulingportal/technicianAppointment";
import AscendingSortIcon from "../../../assets/icons/sort-ascending.png";
import DescendingSortIcon from "../../../assets/icons/sort-descending.png";
import NoSort from "../../../assets/icons/no-sort.png";
import {
  Wrapper,
  CustomisedCell,
  MobileCaseListTable,
  MobileCaseListTableRow,
  MobileCaseListTableData,
  MobileCaseListTableDataWrapper,
} from "../../cases/ViewCases";
import moment from "moment";
import { caseSubjectField } from "../common/JobsPayload";
import MobileSkeletonLoader from "./MobileSkeletonLoader";
import { DEVICE_BREAKPOINTS } from "../../../constants/appConstants";
import { getPendoIdPrefix } from "../../../util/cfstPendoUtils";

const { TABLET, DESKTOP } = DEVICE_BREAKPOINTS;

const TechnicianAppointmentsTable = styled(Table)`
  font-family: ${(props) => (props.fontFamily ? props.fontFamily : "neue-haas-grotesk-text")};
  font-style: normal !important;
  font-weight: 400 !important;
  font-size: 12px !important;
  line-height: 18px !important;
  @media (max-width: 1023px) {
    margin-top: 0rem;
  }
  margin-top: 0;
  margin-bottom: 0rem;
  & thead {
    border-bottom: ${(props) => (props.borderBottom ? props.borderBottom : "2px solid #000000")};
    border-top: ${(props) => (props.borderTop ? props.borderTop : "")};
    & th {
      padding-top: 18px !important;
      padding-bottom: 18px !important;
      padding-left: 24px !important;
    }
  }
  & tbody {
    & tr {
      & td {
        padding-top: 15px !important;
        padding-bottom: 15px !important;
        padding-left: 24px !important;
        padding: 0px;
        @media (max-width: 1000px) {
          padding: 1rem 0 0.75rem 0.5rem;
        }
        @media (max-width: 1023px) {
          font-size: 0.75rem;
        }
      }
      & td:nth-child(1) {
        padding-left: 24px !important;
        padding-top: 8px !important;
        padding-bottom: 8px !important;
      }
      & td:nth-child(2) {
        padding-left: 24px !important;
        padding-top: 8px !important;
        padding-bottom: 8px !important;
      }
      & td:nth-child(5) {
        padding-left: 24px !important;
        padding-top: 12px !important;
        padding-bottom: 12px !important;
      }
      & td:last-child {
        padding-left: 22px !important;
        padding-top: 8px !important;
        padding-bottom: 8px !important;
      }
    }
    & tr:last-child {
      border-bottom: 1px solid #d8dada;
    }
  }
`;

const CustomTableHeader = styled(TableHeader)`
  font-family: "neue-haas-grotesk-text";
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  color: #000000;
  padding: 18px 0 18px 1.5rem !important;
  border-bottom: solid 1px #000;
  @media (max-width: 1000px) {
    padding: 1rem 0 0.75rem 0.5rem;
  }
`;

const TableSubData = styled.div`
  font-size: 12px;
  color: #6f7171;
  font-weight: 400;
  & span {
    padding-right: 5px;
  }
`;

const SortIconStyle = styled.img`
  width: 11px;
  position: absolute;
  margin-left: 5px;
  margin-top: 4px;
`;

const ViewButton = styled(CustomisedTertiaryButton)`
  color: #006fc1;
  font-size: 12px;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: none;
  font-weight: 700;
  line-height: 16px;
  height: 32px;
  @media (max-width: 1023) {
    width: auto;
  }
`;

const TableFallbackMessage = styled.div`
  text-align: center;
  margin-top: 32px;
  margin-bottom: 32px;
  gap: 8px;
  display: grid;
  font-family: neue-haas-grotesk-text;
  & h4 {
    text-align: center;
    line-height: 20px;
    font-weight: 700;
    font-size: 14px;
    padding: 0px;
    margin: 0px;
  }
  & p {
    line-height: 20px;
    font-weight: 400;
    font-size: 14px;
    padding: 0px;
    margin: 0px;
  }

  @media (max-width: 767px) {
    margin-top: 40px;
    margin-bottom: 40px;
  }
`;

const CPTooltip = styled(Tooltip)`
  & span {
    & span {
      word-break: break-word;
      font-family: "neue-haas-grotesk-text" !important;
      font-style: normal;
      font-weight: 400;
      font-size: 12px !important;
      line-height: 18px !important;
    }
  }
`;

const CustomisedDiv = styled.div`
  font-family: ${(props) => (props.fontFamily ? props.fontFamily : "neue-haas-grotesk-text")};
  max-width: ${(props) => (props.maxWidth)};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  padding: 0px !important;
  font-family: "neue-haas-grotesk-text";
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
`;

function TechnicianAppointmentTable({ dashboardLoader }) {
  const { activePage, recordsPerPage, appointmentList, pageRangeDisplayed, totalNumberOfRecords, appointmentsPerPage, filteredAppointmentsList } =
    useSelector((state) => state.TECHNICIANAPPOINTMENT);
  const { windowSize } = useSelector((state) => state.ACCOUNT);

  const dispatch = useDispatch();

  const [dateFilter, setDateFilter] = useState("");
  const [appointmentFilter, setAppointmentFilter] = useState("All");
  const [isFilterClearEnabled, setIsFilterClearEnabled] = useState(false);
  const [isDateAscending, setIsDateAscending] = useState("NO");
  const [ariaLabelText, setAriaLabelText] = useState("");

  useEffect(() => {
    if (dateFilter || appointmentFilter) {
      setIsFilterClearEnabled(true);
    } else {
      setIsFilterClearEnabled(false);
    }
  }, [dateFilter, appointmentFilter]);

  let navigate = useNavigate();

  const technicianAppointmentFilter = () => {
    let appointmentFilterData;
    if (appointmentFilter !== "All") {
      if (appointmentFilter && dateFilter) {
        appointmentFilterData = appointmentList.filter((appointment) => {
          return appointment.status === appointmentFilter && appointment.scheduledDate === dateFilter;
        });
      } else {
        appointmentFilterData = appointmentList.filter((appointment) => {
          return appointment.status === appointmentFilter || appointment.scheduledDate === dateFilter;
        });
      }
    } else if (appointmentFilter === "All" && dateFilter) {
      appointmentFilterData = appointmentList.filter((appointment) => {
        return appointment.status === appointmentFilter || appointment.scheduledDate === dateFilter;
      });
    } else {
      appointmentFilterData = appointmentList;
    }

    dispatch(filteredAppointmentList(appointmentFilterData));
  };

  const clearDateNdStatusFilter = () => {
    setDateFilter("");
    setAppointmentFilter("All");
    dispatch(filteredAppointmentList(appointmentList));
    setIsFilterClearEnabled(false);
  };

  const trackAppointment = (appointmentId) => {
    navigate(`/account/technicianAppointments/viewAppointmentDetails/${appointmentId}`);
  };

  function compareAppointmentDates(firstValue, secondValue) {
    if (firstValue.isBefore(secondValue)) {
      return -1;
    }
    if (firstValue.isAfter(secondValue)) {
      return 1;
    }
    return 0;
  }

  const tableSort = (fieldName) => {
    let sortedData;
    if (!isDateAscending || (isDateAscending === "NO" && fieldName === "dateSorting")) {
      sortedData = filteredAppointmentsList.sort((a, b) => {
        const dateA = moment(a.scheduledDate + " " + a.scheduledTime, "MM/DD/YYYY HH:mm A");
        const dateB = moment(b.scheduledDate + " " + b.scheduledTime, "MM/DD/YYYY HH:mm A");
        return compareAppointmentDates(dateA, dateB);
      });
      setIsDateAscending(true);
      setTimeout(() => {
        setAriaLabelText("Appointment date/time. Sorted in Ascending order");
      }, 100);
    } else if (isDateAscending && fieldName === "dateSorting") {
      sortedData = filteredAppointmentsList.sort((a, b) => {
        const dateA = moment(a.scheduledDate + " " + a.scheduledTime, "MM/DD/YYYY HH:mm A");
        const dateB = moment(b.scheduledDate + " " + b.scheduledTime, "MM/DD/YYYY HH:mm A");
        return compareAppointmentDates(dateB, dateA);
      });
      setIsDateAscending(false);
      setTimeout(() => {
        setAriaLabelText("Appointment date/time. Sorted in Descending order");
      }, 100);
    }
    dispatch(filteredAppointmentList(sortedData));
  };

  const handleLoadTableData = (activePageNo = activePage, data = filteredAppointmentsList) => {
    const lastRecordIndex = activePageNo * recordsPerPage;
    const firstRecordIndex = lastRecordIndex - recordsPerPage;
    let inputData = [...data];
    let slicedData = inputData.slice(firstRecordIndex, lastRecordIndex);
    dispatch(setTotalNumberOfRecords(inputData.length));
    dispatch(setAppointmentPerPage(slicedData));
  };

  const handlePageChange = (pageNumber) => {
    let number = Number.parseInt(pageNumber);
    dispatch(setActivePage(number));
    handleLoadTableData(number);
  };

  function jobTimeDuration(mins, fromTime) {
    //calculating job end time based on job Duration
    let h = (mins / 60) | 0,
      m = mins % 60 | 0;
    const time1 = moment(fromTime, "h:mm:ss a");
    const time2 = moment(moment.utc().hours(h).minutes(m).format("hh:mm A"), "h:mm:ss a");
    const result = time1.add(time2.get("hours"), "hours").add(time2.get("minutes"), "minutes").add(time2.get("seconds"), "seconds");
    return result.format("h:mm A");
  }

  const styles = {
    divider: {
      border: "solid 1px",
      marginTop: "12px",
      borderColor: "#dfdfdf",
    },
  };

  const isSmallScreen = windowSize.width < 1200;
  const smallScreenWidths = {
    frst: "8%",
    second: "12%",
    third: "21%",
    fourth: "10%",
    fifth: "18%",
  };

  const largeScreenWidths = {
    frst: "20%",
    second: "21%",
    third: "20%",
    fourth: "14%",
    fifth: "25%",
  };

  const widths = isSmallScreen ? smallScreenWidths : largeScreenWidths;
  const frstColumnWidth = widths.frst;
  const secondColumnWidth = widths.second;
  const thirdColumnWidth = widths.third;
  const fourthColumnWidth = widths.fourth;
  const fifthColumnWidth = widths.fifth;

  function formatAddress2(address2) {
    if (address2) {
      return `, ${address2}`;
    }
    return "";
  }

  function renderSortIcon(isDateAscending) {
    if (isDateAscending === "NO") {
      return <SortIconStyle alt="search-icon" src={NoSort} aria-hidden="true" tabIndex="-1" />;
    }
    if (isDateAscending) {
      return <SortIconStyle1 alt="ascending-icon" src={DescendingSortIcon} aria-hidden="true" tabIndex="-1" />;
    }
    if (!isDateAscending) {
      return <SortIconStyle1 alt="descending-icon" src={AscendingSortIcon} aria-hidden="true" tabIndex="-1" />;
    }
    return <></>;
  }

  return (
    <>
      <TechnicianAppointmentFilter
        dateFilter={dateFilter}
        appointmentFilter={appointmentFilter}
        isFilterClearEnabled={isFilterClearEnabled}
        setDateFilter={setDateFilter}
        setAppointmentFilter={setAppointmentFilter}
        clearDateNdStatusFilter={clearDateNdStatusFilter}
        technicianAppointmentFilter={technicianAppointmentFilter}
        windowSize={windowSize}
      />
      <TextWrapper fontStyle="normal" fontWeight="400" fontSize="14px" lineHeight="20px" marginBottom="16px" marginTop="0">
        This list contains only appointments created online.
      </TextWrapper>

      {windowSize.width >= DESKTOP ? (
        <>
          <TechnicianAppointmentsTable
            fontFamily="neue-haas-grotesk-text"
            topLine="none"
            aria-label="View Appointments Table"
            aria-colcount="6"
            borderTop={"2px solid #000000"}
            borderBottom={"1px solid #000000"}
          >
            <thead style={{ height: "3.5rem" }}>
              <tr>
                <CustomTableHeader width={frstColumnWidth} scope="col">
                  Service #
                </CustomTableHeader>
                <CustomTableHeader width={secondColumnWidth} scope="col">
                  <button
                    id="cfst-appt-date-time-sort"
                    className="sortableCol"
                    tabIndex="0"
                    aria-label={"Appointment date/time. Click to sort the list"}
                    onClick={() => tableSort("dateSorting")}
                  >
                    Appointment date/time
                    {renderSortIcon(isDateAscending)}
                    <div className="sr-only" role="status" aria-atomic="true">
                      {ariaLabelText}
                    </div>
                  </button>
                </CustomTableHeader>
                <CustomTableHeader width={thirdColumnWidth} scope="col">
                  Appointment location
                </CustomTableHeader>
                <CustomTableHeader width={fourthColumnWidth} scope="col">
                  Contact
                </CustomTableHeader>
                <CustomTableHeader width={"12.5%"} scope="col">
                  Status
                </CustomTableHeader>
                <CustomTableHeader width={fifthColumnWidth} scope="col">
                  Details
                </CustomTableHeader>
              </tr>
            </thead>
            {dashboardLoader ? (
              <TableBody bottomLine={true} hasHover={false} striped={false}>
                <tr>
                  <td colSpan="6" style={{ padding: "0px" }}>
                    <SkeletonLoader width="100%" height="2rem" />
                    <hr style={styles.divider} />
                    <SkeletonLoader height="2rem" />
                    <hr style={styles.divider} />
                    <SkeletonLoader height="2rem" />
                    <hr style={styles.divider} />
                    <SkeletonLoader height="2rem" />
                    <hr style={styles.divider} />
                    <SkeletonLoader height="2rem" />
                    <hr style={styles.divider} />
                    <SkeletonLoader width="100%" marginTop="1.5rem" />
                  </td>
                </tr>
              </TableBody>
            ) : (
              <TableBody bottomLine={true} hasHover={false} striped={false}>
                <>
                  {appointmentList.length === 0 && (
                    <tr>
                      <td colSpan="6" style={{ padding: "0px" }}>
                        <TableFallbackMessage>
                          <h4>{"No appointments"}</h4>
                          <p>{"No technician appointments are scheduled."}</p>
                        </TableFallbackMessage>
                      </td>
                    </tr>
                  )}

                  {appointmentsPerPage.length === 0 && appointmentList.length !== 0 && windowSize.width > 681 && (
                    <tr>
                      <td colSpan="6" style={{ padding: "0px" }}>
                        <TableFallbackMessage>
                          <h4>{"No results"}</h4>
                          <p>{"Try adjusting your search."}</p>
                        </TableFallbackMessage>
                      </td>
                    </tr>
                  )}

                  {appointmentsPerPage?.map((appointment) => {
                    return (
                      <TableRow key={appointment.id}>
                        <>
                          <CustomisedCell aria-colindex="1">
                            {appointment.id}
                            <TableSubData>
                              <span>{caseSubjectField(appointment.transfer, appointment.removal)}</span>
                            </TableSubData>
                          </CustomisedCell>
                          <CustomisedCell aria-colindex="2">
                            <span className="sr-only">Appointment Date</span>
                            {appointment.scheduledTime && appointment.jobDuration && appointment.scheduledDate ? (
                              <>
                                {appointment.scheduledDate}
                                <TableSubData>
                                  <span className="sr-only">Appointment Time</span>
                                  {`${appointment.scheduledTime} - ${jobTimeDuration(appointment.jobDuration, appointment.scheduledTime)}`}
                                </TableSubData>
                              </>
                            ) : (
                              "--"
                            )}
                          </CustomisedCell>
                          <CustomisedCell aria-colindex="3">
                            <CPTooltip
                              renderAnchorElement={() => (
                                <CustomisedDiv maxWidth="15vw" onClick={windowSize.width >= TABLET && "return false;"}                            >
                                  {`${appointment.address1}${formatAddress2(appointment.address2)}, ${appointment.city}, ${
                                    appointment.state
                                  } ${appointment.zipCode}, ${appointment.country}`}
                                </CustomisedDiv>
                              )}
                            >
                              {appointment.address1},<br />
                              {appointment.address2 && `${appointment.address2},`} {appointment.address2 && <br />}
                              {`${appointment.city}, ${appointment.state} ${appointment.zipCode}, ${appointment.country}`}
                            </CPTooltip>
                          </CustomisedCell>

                          <CustomisedCell aria-colindex="4">
                            <CPTooltip
                              renderAnchorElement={() => (
                                <CustomisedDiv maxWidth="6vw" onClick={windowSize.width >= TABLET && "return false;"}>
                                  {appointment.contactName}
                                </CustomisedDiv>
                              )}
                            >
                              {appointment.contactName}
                            </CPTooltip>
                          </CustomisedCell>
                          <CustomisedCell aria-colindex="5">
                            {appointment.status != null ? (
                              <Chip fontSize="12px" status={appointment.status} cfst="true">
                                {appointment.status}
                              </Chip>
                            ) : (
                              "null"
                            )}
                          </CustomisedCell>
                          <CustomisedCell aria-colindex="6">
                            <ViewButton
                              id={`${getPendoIdPrefix()}-appt-list-view-appt`}
                              onClick={() => trackAppointment(appointment.id)}
                              aria-label={`Service #${appointment.id}
                              Appointment date ${appointment.scheduledDate} and time ${appointment.scheduledTime} - ${jobTimeDuration(
                                appointment.jobDuration,
                                appointment.scheduledTime
                              )}
                              Appointment location ${appointment.address1} ${appointment.address2} ${appointment.city} ${appointment.state} ${
                                appointment.zipCode
                              } ${appointment.country}
                              Contact ${appointment.contactName}
                              Status ${appointment.status}
                              View button. Click to view appointment details
                              `}
                              tabIndex={0}
                              onKeyDown={(e) => e.key === "Enter" || (e.key === " " && trackAppointment(appointment.id))}
                            >
                              VIEW
                            </ViewButton>
                          </CustomisedCell>
                        </>
                      </TableRow>
                    );
                  })}
                </>
              </TableBody>
            )}
          </TechnicianAppointmentsTable>
          {appointmentList.length >= 0 && appointmentsPerPage.length !== 0 && (
            <PageAlignment
              activePage={activePage}
              recordsPerPage={recordsPerPage}
              totalNumberOfRecords={totalNumberOfRecords}
              pageRangeDisplayed={pageRangeDisplayed}
              handlePageChange={handlePageChange}
            />
          )}
        </>
      ) : (
        <>
          {" "}
          <Wrapper style={{ borderTop: "2px solid black", width: "100%" }}>
            {dashboardLoader && <MobileSkeletonLoader width={windowSize.width} />}
            {appointmentsPerPage.length !== 0 && (
              <>
                {appointmentsPerPage?.map((appointment) => {
                  return (
                    <MobileCaseListTable key={appointment.id}>
                      <MobileCaseListTableRow>
                        <MobileCaseListTableDataWrapper>
                          <MobileCaseListTableData tabIndex="0" aria-label="Case number" className="tableHeader">
                            {"Service #"}
                          </MobileCaseListTableData>
                        </MobileCaseListTableDataWrapper>
                        <MobileCaseListTableDataWrapper>
                          <CustomisedCell fontSize="14px" lineHeight="20px" style={{ display: "block" }}>
                            {appointment.id}
                            <TableSubData>
                              <span>{caseSubjectField(appointment.transfer, appointment.removal)}</span>
                            </TableSubData>
                          </CustomisedCell>
                        </MobileCaseListTableDataWrapper>
                      </MobileCaseListTableRow>
                      <MobileCaseListTableRow>
                        <MobileCaseListTableDataWrapper>
                          <MobileCaseListTableData tabIndex="0" aria-label="Subject" className="tableHeader">
                            {"Appointment date/time"}
                          </MobileCaseListTableData>
                        </MobileCaseListTableDataWrapper>
                        <MobileCaseListTableDataWrapper>
                          <CustomisedCell fontSize="14px" lineHeight="20px" style={{ display: "block" }}>
                            {appointment.scheduledTime && appointment.jobDuration && appointment.scheduledDate ? (
                              <>
                                {appointment.scheduledDate}
                                <TableSubData>
                                  {`${appointment.scheduledTime} - ${jobTimeDuration(appointment.jobDuration, appointment.scheduledTime)}`}
                                </TableSubData>
                              </>
                            ) : (
                              "--"
                            )}
                          </CustomisedCell>
                        </MobileCaseListTableDataWrapper>
                      </MobileCaseListTableRow>
                      <MobileCaseListTableRow>
                        <MobileCaseListTableDataWrapper>
                          <MobileCaseListTableData tabIndex="0" aria-label="Details" className="tableHeader">
                            {"Appointment location"}
                          </MobileCaseListTableData>
                        </MobileCaseListTableDataWrapper>
                        <MobileCaseListTableDataWrapper>
                          <CustomisedCell aria-colindex="4">
                            {`${appointment.address1}${formatAddress2(appointment.address2)}, ${appointment.city}, ${
                              appointment.state
                            } ${appointment.zipCode}, ${appointment.country}`}
                          </CustomisedCell>
                        </MobileCaseListTableDataWrapper>
                      </MobileCaseListTableRow>
                      <MobileCaseListTableRow>
                        <MobileCaseListTableDataWrapper>
                          <MobileCaseListTableData tabIndex="0" aria-label="Created" className="tableHeader">
                            {"Contact"}
                          </MobileCaseListTableData>
                        </MobileCaseListTableDataWrapper>
                        <MobileCaseListTableDataWrapper>
                          {" "}
                          <CustomisedCell> {appointment.contactName}</CustomisedCell>
                        </MobileCaseListTableDataWrapper>
                      </MobileCaseListTableRow>

                      <MobileCaseListTableRow>
                        <MobileCaseListTableDataWrapper>
                          <MobileCaseListTableData>
                            <MobileCaseListTableData className="tableHeader">{"Status"}</MobileCaseListTableData>
                          </MobileCaseListTableData>
                        </MobileCaseListTableDataWrapper>
                        <MobileCaseListTableDataWrapper>
                          <Chip fontSize="12px" status={appointment.status} cfst="true">
                            {appointment.status}
                          </Chip>
                        </MobileCaseListTableDataWrapper>
                      </MobileCaseListTableRow>
                      <MobileCaseListTableRow>
                        <ViewButton id={`${getPendoIdPrefix()}-appt-list-view-appt`} onClick={() => trackAppointment(appointment.id)}>
                          VIEW
                        </ViewButton>
                      </MobileCaseListTableRow>
                    </MobileCaseListTable>
                  );
                })}

                {appointmentList.length !== 0 && appointmentsPerPage.length !== 0 && (
                  <PageAlignment
                    activePage={activePage}
                    recordsPerPage={recordsPerPage}
                    totalNumberOfRecords={totalNumberOfRecords}
                    pageRangeDisplayed={pageRangeDisplayed}
                    handlePageChange={handlePageChange}
                  />
                )}
              </>
            )}

            {appointmentsPerPage.length === 0 && appointmentList.length !== 0 && (
              <>
                <TableFallbackMessage>
                  <h4>{"No results"}</h4>
                  <p>{"Try adjusting your search."}</p>
                </TableFallbackMessage>
                <hr />
              </>
            )}

            {appointmentList.length === 0 && !dashboardLoader && (
              <>
                <TableFallbackMessage>
                  <h4>{"No appointments"}</h4>
                  <p>{"No technician appointments are scheduled."}</p>
                </TableFallbackMessage>
                <hr />
              </>
            )}
          </Wrapper>
        </>
      )}
    </>
  );
}
TechnicianAppointmentTable.propTypes = {
  dashboardLoader: PropTypes.bool.isRequired,
};

export default TechnicianAppointmentTable;